import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import API from "@/lib/api";
import { ChevronRight, Loader2, MapPin, Package, Plus, Search, SplitSquareVertical, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";

const formatCurrency = (value) => `Rp${Number(value || 0).toLocaleString("id-ID")}`;

const getDisplayProductLabel = (productName = "", brandName = "") => {
  const rawName = String(productName || "").trim();
  const cleanBrandName = String(brandName || "").trim();
  if (!rawName) return cleanBrandName || "Produk";
  const nameWithoutLegacyBrand = rawName.replace(/\s+by\s+.+$/i, "").trim();
  if (!cleanBrandName || cleanBrandName === "No Brand") return nameWithoutLegacyBrand || rawName;
  return `${nameWithoutLegacyBrand || rawName} by ${cleanBrandName}`;
};

const getDisplayVariantValues = (variant = {}) => {
  const technicalKeys = new Set(["variant_id", "product_id", "weight_gram", "weight", "sku", "stock", "price", "cost_price", "sort_order", "status", "source_hash", "created_at", "updated_at", "pricing"]);
  return Object.entries(variant || {})
    .filter(([key, value]) => !technicalKeys.has(key) && value !== null && value !== undefined && String(value).trim() !== "")
    .map(([, value]) => String(value));
};

const PageWrapper = ({ children, isLoading = false, orderId }) => (
  <div className="min-h-screen bg-stone-50 pb-20">
    <Header />
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24 lg:pt-32">
      <Link to={orderId ? `/orders/${orderId}` : "/orders"} className="inline-flex items-center text-sm font-medium text-stone-500 hover:text-stone-900 mb-6 transition-colors group">
        <ChevronRight className="w-4 h-4 mr-1 rotate-180 transition-transform group-hover:-translate-x-1" />
        Kembali ke Detail Pesanan
      </Link>
      {isLoading ? (
        <div className="animate-pulse space-y-6">
          <div className="h-24 bg-white border border-stone-200 rounded-xl" />
          <div className="h-96 bg-white border border-stone-200 rounded-xl" />
        </div>
      ) : children}
    </div>
  </div>
);

export default function OrderShippingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingSplit, setUploadingSplit] = useState(false);
  const [addressBook, setAddressBook] = useState([]);
  const [manualShipments, setManualShipments] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [destinationSearchingId, setDestinationSearchingId] = useState(null);
  const [calculatingShippingId, setCalculatingShippingId] = useState(null);
  const [quoteSavingId, setQuoteSavingId] = useState(null);

  const courierOptions = useMemo(() => {
    const raw = (tenant?.shipping_couriers || "").split(/[,:\s]+/).map((item) => item.trim().toLowerCase()).filter(Boolean);
    return raw.length ? raw : ["jne", "sicepat", "jnt"];
  }, [tenant?.shipping_couriers]);

  const buildInitialManualShipments = (orderData) => {
    const pendingShips = (orderData?.shipments || []).filter((s) => !s.awb);
    if (pendingShips.length > 0) {
      return pendingShips.map((ship) => ({
        id: ship.id,
        name: ship.name || "",
        phone: ship.phone || "",
        address: ship.address || "",
        isDropship: (ship.sender_type || (ship.sender_name || ship.sender_phone ? 'dropshipper' : 'reseller')) === 'dropshipper',
        sender_name: ship.sender_name || "",
        sender_phone: ship.sender_phone || "",
        sender_address: "",
        sender_type: ship.sender_type || (ship.sender_name || ship.sender_phone ? 'dropshipper' : 'reseller'),
        destinationQuery: ship.destination_label || "",
        destinationOptions: [],
        selectedDestination: ship.destination_id ? {
          id: ship.destination_id,
          label: ship.destination_label || "",
          ...(ship.destination_snapshot || {}),
        } : null,
        courier: ship.courier || courierOptions[0] || "jne",
        shippingQuotes: [],
        selectedShippingQuote: ship.shipping_service ? {
          courier_code: ship.courier || "",
          courier_name: (ship.courier || "").toUpperCase(),
          service_code: ship.shipping_service || "",
          service_name: ship.shipping_service || "",
          shipping_cost: Number(ship.shipping_cost || 0),
          shipping_etd: ship.shipping_etd || "",
          raw: ship.shipping_quote_snapshot || {},
        } : null,
        shippingError: "",
        selectedAddressBookId: "",
        postal_code: ship.postal_code || ship.shipping_postal_code || "",
        items: (orderData.items || [])
          .filter((i) => i.shipment_id === ship.id)
          .map((i) => ({ item_id: i.transaction_detail_id, qty: i.quantity })),
      }));
    }
    return [{ id: Date.now(), name: "", phone: "", address: "", postal_code: "", isDropship: false, sender_name: "", sender_phone: "", sender_address: "", sender_type: "reseller", destinationQuery: "", destinationOptions: [], selectedDestination: null, courier: courierOptions[0] || "jne", shippingQuotes: [], selectedShippingQuote: null, shippingError: "", selectedAddressBookId: "", items: [] }];
  };

  const fetchOrder = async () => {
    const res = await API.get(`/orders/${id}`);
    setOrder(res.data);
    setManualShipments(buildInitialManualShipments(res.data));
  };

  useEffect(() => {
    let alive = true;
    API.get("/tenant")
      .then((r) => {
        if (alive) setTenant(r.data || {});
      })
      .catch(() => {});
    API.get("/address-book")
      .then((r) => {
        if (alive) setAddressBook(r.data?.addresses || []);
      })
      .catch(() => {
        if (alive) setAddressBook([]);
      });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await API.get(`/orders/${id}`);
        if (!alive) return;
        setOrder(res.data);
        setManualShipments(buildInitialManualShipments(res.data));
      } catch (err) {
        toast.error("Gagal memuat data pengiriman pesanan.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getLockedQty = (itemId) => {
    const item = order?.items?.find((i) => i.transaction_detail_id === itemId);
    if (!item?.shipment_id) return 0;
    const linkedShipment = (order?.shipments || []).find((s) => s.id === item.shipment_id);
    return linkedShipment?.awb ? (item.quantity || 0) : 0;
  };

  const getRemainingQty = (itemId) => {
    const total = order?.items?.find((i) => i.transaction_detail_id === itemId)?.quantity || 0;
    const locked = getLockedQty(itemId);
    let allocated = 0;
    manualShipments.forEach((ship) => {
      const found = ship.items.find((i) => i.item_id === itemId);
      if (found) allocated += found.qty;
    });
    return Math.max(0, total - locked - allocated);
  };

  const handleAddManualShipment = () => {
    setManualShipments((prev) => [
      ...prev,
      { id: Date.now(), name: "", phone: "", address: "", postal_code: "", isDropship: false, sender_name: "", sender_phone: "", sender_address: "", sender_type: "reseller", destinationQuery: "", destinationOptions: [], selectedDestination: null, courier: courierOptions[0] || "jne", shippingQuotes: [], selectedShippingQuote: null, shippingError: "", selectedAddressBookId: "", items: [] },
    ]);
  };

  const handleRemoveManualShipment = (shipId) => {
    setManualShipments((prev) => prev.filter((s) => s.id !== shipId));
  };

  const handleUpdateManualShipment = (shipId, field, value) => {
    setManualShipments((prev) => prev.map((s) => {
      if (s.id !== shipId) return s;
      const next = { ...s, [field]: value };
      if (field === "destinationQuery") {
        next.selectedDestination = null;
        next.shippingQuotes = [];
        next.selectedShippingQuote = null;
        next.shippingError = "";
      }
      if (field === "courier") {
        next.shippingQuotes = [];
        next.selectedShippingQuote = null;
        next.shippingError = "";
      }
      return next;
    }));
  };

  const applyAddressBookItem = (shipId, addressId) => {
    const selected = addressBook.find((item) => item.id === addressId);
    if (!selected) {
      setManualShipments((prev) => prev.map((s) => s.id === shipId ? {
        ...s,
        selectedAddressBookId: "",
        name: "",
        phone: "",
        address: "",
        postal_code: "",
        destinationQuery: "",
        destinationOptions: [],
        selectedDestination: null,
        shippingQuotes: [],
        selectedShippingQuote: null,
        shippingError: "",
        isDropship: false,
        sender_name: "",
        sender_phone: "",
        sender_address: "",
        sender_type: "reseller",
      } : s));
      return;
    }

    const destination = selected.destination_id ? {
      ...(selected.destination_snapshot || {}),
      id: selected.destination_id,
      label: selected.destination_label || selected.destination_snapshot?.label || "",
    } : null;
    const postalCode = selected.postal_code || "";
    const searchQuery = selected.destination_label || destination?.label || postalCode || "";

    setManualShipments((prev) => prev.map((s) => {
      if (s.id !== shipId) return s;
      return {
        ...s,
        selectedAddressBookId: selected.id,
        name: selected.recipient_name || "",
        phone: selected.recipient_phone || "",
        address: selected.address || "",
        postal_code: postalCode,
        destinationQuery: searchQuery,
        selectedDestination: destination,
        destinationOptions: [],
        shippingQuotes: [],
        selectedShippingQuote: null,
        shippingError: destination ? "" : (postalCode ? "Mencari area RajaOngkir dari kode pos..." : "Alamat ini belum punya area RajaOngkir. Cari tujuan manual untuk hitung ongkir."),
        isDropship: selected.sender_type === "dropship",
        sender_name: selected.sender_type === "dropship" ? (selected.sender_name || "") : "",
        sender_phone: selected.sender_type === "dropship" ? (selected.sender_phone || "") : "",
        sender_address: selected.sender_type === "dropship" ? (selected.sender_address || "") : "",
        sender_type: selected.sender_type === "dropship" ? "dropshipper" : "reseller",
      };
    }));

    if (!destination && postalCode && postalCode.trim().length >= 3) {
      window.setTimeout(() => handleSearchDestination(shipId, postalCode, { autoSelectSingle: true, silent: true }), 0);
    }
  };

  const getShipmentWeight = (ship) => {
    return (ship.items || []).reduce((acc, item) => {
      const orderItem = order?.items?.find((it) => it.transaction_detail_id === item.item_id);
      const weight = Number(orderItem?.variant?.weight_gram || orderItem?.weight_gram || orderItem?.weight || orderItem?.berat || orderItem?.weight_g || 100) || 100;
      return acc + (Number(item.qty || 0) * weight);
    }, 0) || 100;
  };

  const normalizeShippingQuotes = (raw, courierCode) => {
    const list = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
    return list.map((item, idx) => {
      const serviceCode = item.code || item.service_code || item.service || item.name || `svc-${idx}`;
      const serviceName = item.service || item.name || item.service_code || serviceCode;
      const shippingCostValue = Number(item.cost ?? item.price ?? item.value ?? 0) || 0;
      return {
        id: `${courierCode}-${serviceCode}-${idx}`,
        courier_code: courierCode,
        courier_name: courierCode.toUpperCase(),
        service_code: serviceCode,
        service_name: serviceName,
        shipping_cost: shippingCostValue,
        shipping_etd: item.etd || "",
        shipping_description: item.description || "",
        raw: item,
      };
    }).filter((item) => item.service_name);
  };

  const handleSearchDestination = async (shipId, overrideQuery = null, options = {}) => {
    const ship = manualShipments.find((s) => s.id === shipId);
    const query = (overrideQuery || ship?.destinationQuery || "").trim();
    if (query.length < 3) return toast.error("Masukkan minimal 3 karakter tujuan.");
    setDestinationSearchingId(shipId);
    try {
      const r = await API.get("/shipping/destinations", { params: { search: query, limit: 10, offset: 0 } });
      const destinations = r.data?.data || [];
      setManualShipments((prev) => prev.map((s) => {
        if (s.id !== shipId) return s;
        if (options.autoSelectSingle && destinations.length === 1) {
          const destination = destinations[0];
          const label = destination.label || destination.name || destination.city_name || destination.district_name || destination.subdistrict_name || query;
          return {
            ...s,
            destinationQuery: label,
            destinationOptions: [],
            selectedDestination: destination,
            shippingQuotes: [],
            selectedShippingQuote: null,
            shippingError: "Area RajaOngkir dipilih otomatis dari kode pos.",
          };
        }
        return {
          ...s,
          destinationOptions: destinations,
          selectedDestination: null,
          shippingQuotes: [],
          selectedShippingQuote: null,
          shippingError: destinations.length
            ? (options.autoSelectSingle ? "Pilih area RajaOngkir yang paling sesuai dari hasil pencarian kode pos." : "")
            : "Area RajaOngkir belum ditemukan dari kode pos ini. Coba cari manual dengan kelurahan/kecamatan.",
        };
      }));
    } catch (err) {
      const message = err.response?.data?.detail || "Gagal mencari tujuan pengiriman.";
      if (!options.silent) toast.error(message);
      setManualShipments((prev) => prev.map((s) => s.id === shipId ? { ...s, shippingError: message } : s));
    } finally {
      setDestinationSearchingId(null);
    }
  };

  const handleSelectDestination = (shipId, destination) => {
    const label = destination.label || destination.name || destination.city_name || destination.district_name || destination.subdistrict_name || "";
    setManualShipments((prev) => prev.map((s) => s.id === shipId ? { ...s, selectedDestination: destination, destinationQuery: label, destinationOptions: [], shippingQuotes: [], selectedShippingQuote: null, shippingError: "" } : s));
  };

  const handleCalculateShipping = async (shipId) => {
    const ship = manualShipments.find((s) => s.id === shipId);
    if (!ship?.selectedDestination?.id) return toast.error("Pilih tujuan RajaOngkir dulu.");
    const courierCode = ship.courier || courierOptions[0] || "jne";
    setCalculatingShippingId(shipId);
    try {
      const r = await API.post("/shipping/calculate", {
        destination: String(ship.selectedDestination.id),
        weight: getShipmentWeight(ship),
        courier: courierCode,
        price: "lowest",
      });
      const quotes = normalizeShippingQuotes(r.data, courierCode);
      setManualShipments((prev) => prev.map((s) => s.id === shipId ? { ...s, courier: courierCode, shippingQuotes: quotes, selectedShippingQuote: null, shippingError: quotes.length ? "" : "Tidak ada service ongkir." } : s));
    } catch (err) {
      const detail = err.response?.data?.detail || "Gagal hitung ongkir.";
      const friendly = String(detail).includes("Origin shipping belum diset")
        ? "Origin shipping belum diset di Admin Settings > Shipping Settings. Admin perlu memilih lokasi asal pengiriman dulu sebelum cek ongkir."
        : detail;
      setManualShipments((prev) => prev.map((s) => s.id === shipId ? { ...s, shippingQuotes: [], selectedShippingQuote: null, shippingError: friendly } : s));
    } finally {
      setCalculatingShippingId(null);
    }
  };

  const handleSelectShippingQuote = async (shipId, quote) => {
    const ship = manualShipments.find((s) => s.id === shipId);
    setManualShipments((prev) => prev.map((s) => s.id === shipId ? { ...s, selectedShippingQuote: quote } : s));

    // Existing shipment rows have string ids from backend (ship_xxx). New unsaved blocks
    // will be persisted together with the full manual split submit.
    if (!ship || typeof ship.id !== "string") return;

    const payload = {
      courier_code: quote.courier_code || ship.courier || "",
      courier_name: quote.courier_name || (quote.courier_code || ship.courier || "").toUpperCase(),
      service_code: quote.service_code || "",
      service_name: quote.service_name || "",
      shipping_cost: Number(quote.shipping_cost || 0),
      shipping_etd: quote.shipping_etd || "",
      origin_snapshot: {},
      destination_snapshot: ship.selectedDestination || {},
      shipping_quote_snapshot: quote.raw || quote || {},
      shipping_weight_grams: getShipmentWeight(ship),
    };

    setQuoteSavingId(shipId);
    try {
      const r = await API.put(`/orders/${id}/shipments/${ship.id}/shipping-quote`, payload);
      if (r.data?.order) setOrder(r.data.order);
      toast.success("Ongkir tersimpan ke transaksi.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan ongkir.");
    } finally {
      setQuoteSavingId(null);
    }
  };

  const handleIncrementManualItem = (shipId, itemId) => {
    const rem = getRemainingQty(itemId);
    if (rem <= 0) return toast.error("Kuantitas produk ini sudah habis dialokasikan.");
    setManualShipments((prev) => prev.map((s) => {
      if (s.id !== shipId) return s;
      const existing = s.items.find((i) => i.item_id === itemId);
      const newItems = existing
        ? s.items.map((i) => i.item_id === itemId ? { ...i, qty: i.qty + 1 } : i)
        : [...s.items, { item_id: itemId, qty: 1 }];
      return { ...s, items: newItems };
    }));
  };

  const handleDecrementManualItem = (shipId, itemId) => {
    setManualShipments((prev) => prev.map((s) => {
      if (s.id !== shipId) return s;
      const existing = s.items.find((i) => i.item_id === itemId);
      if (!existing) return s;
      const newItems = existing.qty <= 1
        ? s.items.filter((i) => i.item_id !== itemId)
        : s.items.map((i) => i.item_id === itemId ? { ...i, qty: i.qty - 1 } : i);
      return { ...s, items: newItems };
    }));
  };

  const handleSubmitManualSplit = async () => {
    if (manualShipments.length === 0) return toast.error("Tidak ada data penerima.");
    for (const s of manualShipments) {
      if (!s.name || !s.phone || !s.address) return toast.error("Nama, nomor HP, dan alamat wajib diisi untuk semua penerima.");
      if (s.isDropship && (!s.sender_name || !s.sender_phone)) return toast.error(`Nama dan nomor HP pengirim dropship wajib diisi untuk penerima ${s.name}.`);
      if (s.items.length === 0) return toast.error(`Penerima ${s.name} belum memiliki barang.`);
    }

    const payload = manualShipments.map((s) => {
      const selectedDestination = s.selectedDestination || null;
      const selectedQuote = s.selectedShippingQuote || null;
      return {
        name: s.name,
        phone: s.phone,
        address: s.address,
        sender_name: s.isDropship ? s.sender_name : "",
        sender_phone: s.isDropship ? s.sender_phone : "",
        sender_address: "",
        sender_type: s.isDropship ? "dropshipper" : "reseller",
        destination_id: selectedDestination?.id ? String(selectedDestination.id) : "",
        destination_label: selectedDestination ? (selectedDestination.label || selectedDestination.name || selectedDestination.city_name || selectedDestination.district_name || selectedDestination.subdistrict_name || "") : "",
        destination_snapshot: selectedDestination || {},
        courier: selectedQuote?.courier_code || s.courier || "",
        shipping_service: selectedQuote?.service_name || selectedQuote?.service_code || "",
        shipping_cost: Number(selectedQuote?.shipping_cost || 0),
        shipping_etd: selectedQuote?.shipping_etd || "",
        shipping_weight_grams: getShipmentWeight(s),
        shipping_quote_snapshot: selectedQuote?.raw || selectedQuote || {},
        items: s.items,
      };
    });

    setUploadingSplit(true);
    try {
      await API.post(`/orders/${id}/split-manual`, { shipments: payload });
      toast.success("Pengaturan pengiriman berhasil disimpan.");
      await fetchOrder();
      navigate(`/orders/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan pengaturan pengiriman.");
    } finally {
      setUploadingSplit(false);
    }
  };

  if (loading) return <PageWrapper isLoading orderId={id} />;

  if (!order) {
    return (
      <PageWrapper orderId={id}>
        <div className="text-center py-20 bg-white border border-stone-200 rounded-xl">
          <Package className="w-12 h-12 text-stone-200 mx-auto mb-4" />
          <p className="text-stone-500">Pesanan tidak ditemukan.</p>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper orderId={id}>
      <div className="space-y-6">
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">Pengaturan Pengiriman</p>
              <h1 className="text-2xl font-serif text-stone-900 mt-1">Atur Pengiriman Order</h1>
              <p className="text-sm text-stone-500 mt-2">Order <span className="font-semibold text-stone-800">{order.transaction_id || order.id}</span>. Halaman ini dipisahkan agar nanti fitur hitung ongkir bisa ditambahkan dengan lebih rapi.</p>
            </div>
            <div className="rounded-lg bg-primary/5 text-primary px-4 py-2 text-xs font-bold uppercase tracking-wider border border-primary/10 w-fit">
              Input Manual
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 bg-white border border-stone-200 rounded-2xl p-5 shadow-sm h-fit lg:sticky lg:top-24">
              <h3 className="font-semibold text-stone-800 mb-3 text-sm flex items-center gap-2"><SplitSquareVertical className="w-4 h-4" /> Sisa Belum Dialokasi</h3>
              <div className="space-y-3">
                {order?.items?.map((it) => {
                  const rem = getRemainingQty(it.transaction_detail_id);
                  if (rem <= 0) return null;
                  return (
                    <div key={it.transaction_detail_id} className="bg-stone-50 p-3 rounded-lg border border-stone-100 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-stone-800 line-clamp-1" title={getDisplayProductLabel(it.product_name, it.brand_name)}>{getDisplayProductLabel(it.product_name, it.brand_name)}</p>
                        {getDisplayVariantValues(it.variant).length > 0 && <p className="text-[10px] text-stone-400">{getDisplayVariantValues(it.variant).join(", ")}</p>}
                      </div>
                      <span className="text-xs font-bold text-primary shrink-0">Sisa {rem}</span>
                    </div>
                  );
                })}
                {order?.items?.every((it) => getRemainingQty(it.transaction_detail_id) <= 0) && (
                  <p className="text-xs text-stone-500 text-center py-4 bg-stone-50 rounded border border-stone-100">Semua produk sudah dialokasi.</p>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              {manualShipments.map((ship, idx) => (
                <div key={ship.id} className="bg-white border border-stone-200 rounded-2xl p-5 shadow-sm relative">
                  {manualShipments.length > 1 && (
                    <button onClick={() => handleRemoveManualShipment(ship.id)} className="absolute top-4 right-4 text-stone-300 hover:text-red-500 bg-white" title="Hapus penerima">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <h4 className="font-semibold text-stone-800 text-sm mb-4">Penerima #{idx + 1}</h4>
                  {addressBook.length > 0 && (
                    <div className="mb-4 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                      <label className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1"><MapPin className="w-3 h-3" /> Pick from Address Book</label>
                      <select value={ship.selectedAddressBookId || ""} onChange={(e) => applyAddressBookItem(ship.id, e.target.value)} className="mt-2 w-full text-sm bg-white border border-emerald-100 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none">
                        <option value="">Isi Alamat Tujuan Pengiriman</option>
                        {addressBook.map((address) => (
                          <option key={address.id} value={address.id}>
                            {(address.label || "Alamat")} — {address.recipient_name || "-"} {address.is_default ? "(Default)" : ""}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-emerald-700/80 mt-2">Data penerima, alamat, dropship, kode pos, dan tujuan RajaOngkir akan terisi jika tersedia. Jika belum ada tujuan RajaOngkir, sistem otomatis mencari dari kode pos.</p>
                    </div>
                  )}
                  <div className="grid sm:grid-cols-2 gap-3 mb-4">
                    <input value={ship.name} onChange={(e) => handleUpdateManualShipment(ship.id, "name", e.target.value)} placeholder="Nama Penerima *" className="text-sm bg-stone-50 border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none" />
                    <input value={ship.phone} onChange={(e) => handleUpdateManualShipment(ship.id, "phone", e.target.value)} placeholder="Nomor HP *" className="text-sm bg-stone-50 border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none" />
                    <textarea value={ship.address} onChange={(e) => handleUpdateManualShipment(ship.id, "address", e.target.value)} placeholder="Alamat Lengkap *" rows="3" className="sm:col-span-2 text-sm bg-stone-50 border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none" />
                    <input value={ship.postal_code || ""} onChange={(e) => handleUpdateManualShipment(ship.id, "postal_code", e.target.value)} placeholder="Kode Pos" className="text-sm bg-stone-50 border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none" />
                  </div>

                  <div className="mb-4 bg-stone-50 p-3 rounded-lg border border-stone-200 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider flex items-center gap-1"><MapPin className="w-3 h-3" /> Cek Ongkir</p>
                        <p className="text-[11px] text-stone-400 mt-1">Cari kelurahan/kecamatan/kota penerima untuk hitung ongkir.</p>
                      </div>
                      <span className="text-[11px] font-semibold text-stone-500 shrink-0">Berat {getShipmentWeight(ship)}g</span>
                    </div>
                    <div className="flex gap-2">
                      <input value={ship.destinationQuery || ""} onChange={(e) => handleUpdateManualShipment(ship.id, "destinationQuery", e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") handleSearchDestination(ship.id); }} placeholder="Cari tujuan, contoh: Cilandak / Jakarta / kode pos" className="flex-1 text-sm bg-white border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none" />
                      <button type="button" onClick={() => handleSearchDestination(ship.id)} disabled={destinationSearchingId === ship.id} className="px-3 rounded-lg bg-stone-900 text-white text-sm font-semibold disabled:opacity-50">
                        {destinationSearchingId === ship.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      </button>
                    </div>
                    {ship.destinationOptions?.length > 0 && (
                      <div className="space-y-2 max-h-48 overflow-y-auto bg-white border border-stone-100 rounded-lg p-2">
                        {ship.destinationOptions.map((dest) => (
                          <button key={dest.id} type="button" onClick={() => handleSelectDestination(ship.id, dest)} className="w-full text-left rounded-md border border-stone-100 hover:border-primary hover:bg-primary/5 px-3 py-2 transition-colors">
                            <div className="text-xs font-semibold text-stone-800">{dest.label || dest.name || dest.city_name || dest.district_name || dest.subdistrict_name}</div>
                            <div className="text-[10px] text-stone-500 mt-0.5">{[dest.province_name, dest.city_name, dest.district_name, dest.subdistrict_name, dest.zip_code].filter(Boolean).join(" · ")}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    {ship.selectedDestination && (
                      <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs text-stone-700">
                        <div className="font-semibold text-stone-900">Tujuan dipilih: {ship.selectedDestination.label || ship.selectedDestination.name || ship.selectedDestination.subdistrict_name}</div>
                        <div className="text-[11px] text-stone-500 mt-1">{[ship.selectedDestination.province_name, ship.selectedDestination.city_name, ship.selectedDestination.district_name, ship.selectedDestination.subdistrict_name, ship.selectedDestination.zip_code].filter(Boolean).join(" · ")}</div>
                        {(ship.selectedShippingQuote || ship.courier) && (
                          <div className="mt-3 pt-3 border-t border-primary/10 grid sm:grid-cols-3 gap-2">
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Ekspedisi</p>
                              <p className="font-semibold text-stone-800 uppercase">{ship.selectedShippingQuote?.courier_name || ship.selectedShippingQuote?.courier_code || ship.courier || "-"}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Jenis Layanan</p>
                              <p className="font-semibold text-stone-800">{ship.selectedShippingQuote?.service_name || ship.selectedShippingQuote?.service_code || ship.shipping_service || "-"}</p>
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">Ongkir</p>
                              <p className="font-semibold text-primary">{ship.selectedShippingQuote?.shipping_cost ? formatCurrency(ship.selectedShippingQuote.shipping_cost) : "-"}</p>
                              {ship.selectedShippingQuote?.shipping_etd && <p className="text-[10px] text-stone-500">ETD {ship.selectedShippingQuote.shipping_etd}</p>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="grid sm:grid-cols-[1fr_auto] gap-2">
                      <select value={ship.courier || courierOptions[0] || "jne"} onChange={(e) => handleUpdateManualShipment(ship.id, "courier", e.target.value)} className="text-sm bg-white border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none">
                        {courierOptions.map((courier) => <option key={courier} value={courier}>{courier.toUpperCase()}</option>)}
                      </select>
                      <button type="button" onClick={() => handleCalculateShipping(ship.id)} disabled={!ship.selectedDestination || calculatingShippingId === ship.id} className="px-4 py-3 rounded-lg bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider disabled:opacity-50 flex items-center justify-center gap-2">
                        {calculatingShippingId === ship.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Hitung Ongkir
                      </button>
                    </div>
                    {ship.shippingError && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">{ship.shippingError}</p>}
                    {ship.shippingQuotes?.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-2">
                        {ship.shippingQuotes.map((quote) => {
                          const active = ship.selectedShippingQuote?.id === quote.id;
                          return (
                            <button key={quote.id} type="button" onClick={() => handleSelectShippingQuote(ship.id, quote)} className={`text-left rounded-lg border p-3 transition-colors relative ${active ? "border-primary bg-primary/5" : "border-stone-100 bg-white hover:border-stone-200"}`}>
                              {active && quoteSavingId === ship.id && <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-primary" />}
                              <div className="text-xs font-bold text-stone-900 pr-6">{quote.courier_name} {quote.service_name}</div>
                              <div className="text-[11px] text-stone-500 mt-1">ETD {quote.shipping_etd || "-"}</div>
                              <div className="text-sm font-bold text-primary mt-1">{formatCurrency(quote.shipping_cost)}</div>
                              {active && <div className="text-[10px] font-semibold text-primary mt-1">Dipilih{typeof ship.id === "string" ? " · autosave" : ""}</div>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-xs text-stone-700 cursor-pointer mb-2">
                      <input type="checkbox" checked={ship.isDropship} onChange={(e) => handleUpdateManualShipment(ship.id, "isDropship", e.target.checked)} className="text-primary focus:ring-primary" />
                      Send As (Dropshipper)
                    </label>
                    {ship.isDropship && (
                      <div className="bg-stone-50 p-3 rounded-lg border border-stone-200">
                        <div className="grid sm:grid-cols-2 gap-3">
                          <input value={ship.sender_name} onChange={(e) => handleUpdateManualShipment(ship.id, "sender_name", e.target.value)} placeholder="Nama Pengirim *" className="text-sm bg-white border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none" />
                          <input value={ship.sender_phone} onChange={(e) => handleUpdateManualShipment(ship.id, "sender_phone", e.target.value)} placeholder="No HP Pengirim *" className="text-sm bg-white border border-stone-200 rounded-lg p-3 focus:ring-1 focus:ring-primary outline-none" />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-stone-50 p-3 rounded-lg border border-stone-100">
                    <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-2">Barang untuk penerima ini</p>
                    {order?.items?.map((it) => {
                      const alloc = ship.items.find((i) => i.item_id === it.transaction_detail_id)?.qty || 0;
                      const rem = getRemainingQty(it.transaction_detail_id);
                      const locked = getLockedQty(it.transaction_detail_id);
                      if (locked > 0 && alloc === 0) return null;
                      if (alloc === 0 && rem <= 0) return null;
                      return (
                        <div key={it.transaction_detail_id} className="flex items-center justify-between py-2 border-b border-stone-100 last:border-0 gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-stone-700 line-clamp-1">{getDisplayProductLabel(it.product_name, it.brand_name)}</p>
                            {getDisplayVariantValues(it.variant).length > 0 && <p className="text-[10px] text-stone-400">{getDisplayVariantValues(it.variant).join(", ")}</p>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <button onClick={() => handleDecrementManualItem(ship.id, it.transaction_detail_id)} disabled={alloc <= 0} className="w-7 h-7 flex items-center justify-center bg-stone-200 hover:bg-stone-300 rounded text-stone-600 disabled:opacity-30 disabled:cursor-not-allowed">-</button>
                            <span className="text-xs font-bold w-5 text-center">{alloc}</span>
                            <button onClick={() => handleIncrementManualItem(ship.id, it.transaction_detail_id)} disabled={rem <= 0} className="w-7 h-7 flex items-center justify-center bg-primary hover:opacity-90 text-primary-foreground rounded disabled:opacity-30 disabled:cursor-not-allowed">+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <button onClick={handleAddManualShipment} className="w-full border-2 border-dashed border-primary/40 text-primary hover:bg-primary/5 hover:border-primary py-3 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> Tambah Penerima Baru
              </button>

              <button disabled={uploadingSplit} onClick={handleSubmitManualSplit} className="w-full bg-primary hover:opacity-90 text-primary-foreground py-3 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg">
                {uploadingSplit ? "Menyimpan Data..." : "Simpan Pengaturan Pengiriman"}
              </button>
            </div>
          </div>
      </div>
    </PageWrapper>
  );
}
